import { Icon, Image, Tooltip, Typography } from '@smartgift/frontend-toolkit';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useTheme } from 'styled-components';

import { Card, EmptySection, LinearProgressBar } from '@hero/atoms';
import { useGetPopularGifts } from '@hero/services/order-service/hook';
import { formatPercentage } from '@hero/utils/helper';

import * as S from './style';

function AutomationGiftSelection({ automation }) {
  const { code } = useParams();
  const theme = useTheme();
  const { t } = useTranslation();

  const { data: popularGifts } = useGetPopularGifts(
    {
      pagination: {
        page: 1,
        limit: 50
      },
      filters: {
        associationType: 'automation',
        ids: [automation?.id]
      }
    },
    {
      enabled: !!code
    }
  );

  const totalGiftCount = useMemo(
    () => popularGifts?.reduce?.((acc, gift) => acc + gift.count, 0) || 0,
    [popularGifts]
  );

  const giftList = useMemo(() => {
    return popularGifts
      ?.map(gift => {
        const percentage = formatPercentage((gift.count / totalGiftCount) * 100);

        return {
          ...gift,
          percentage
        };
      })
      .filter(gift => gift);
  }, [popularGifts, totalGiftCount]);

  return (
    <Card style={{ gap: 24, flex: 1 }}>
      <Typography variant="body1Bold">{t('giftSelection')}</Typography>
      {giftList?.length > 0 ? (
        <Card style={{ height: '100%', backgroundColor: 'var(--color-bg-weak)' }}>
          <S.ScrollArea>
            {giftList?.map?.(gift => {
              return (
                <S.GiftItem key={gift.id}>
                  <S.ImageWrapper>
                    <Image
                      src={gift.images?.[0]?.url}
                      errorSrc={theme.images.noImage}
                      alt={gift?.name}
                    />
                    {gift?.type === 'charity' && (
                      <Tooltip
                        content={t('charityIconTooltipContent')}
                        tooltipConfig={{ placement: 'top' }}
                      >
                        <S.IconWrapper>
                          <Icon name="f-heart" color="var(--color-error-default)" size={12} />
                        </S.IconWrapper>
                      </Tooltip>
                    )}
                  </S.ImageWrapper>
                  {gift?.name && (
                    <Typography style={{ width: 150 }} lineClamp={2}>
                      {gift.name}
                    </Typography>
                  )}

                  <S.BarArea>
                    <LinearProgressBar
                      backgroundColor="var(--color-error-weaker)"
                      visualParts={[
                        {
                          percentage: gift?.percentage,
                          color: 'var(--color-error-default)'
                        }
                      ]}
                    />
                    <S.Rate>
                      {gift?.percentage}({gift?.count})
                    </S.Rate>
                  </S.BarArea>
                </S.GiftItem>
              );
            })}
          </S.ScrollArea>
        </Card>
      ) : (
        <EmptySection title={t('analyticsGiftSelectionEmptyState')} />
      )}
    </Card>
  );
}

export { AutomationGiftSelection };
